import React, { Component } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { intlShape } from "@ai360/core/intl";

import { Bucket, BucketHeader, Checkbox, Menu } from "~/core";
import { ImportFileInfo } from "~/file-import/model";
import { config as intlConfig } from "~/intl-provider/config";
import { GeometryUtils } from "@ai360/core";

import { messages } from "../../../i18n-messages";
import { AddEditFieldModal } from "../containers/add-edit-field-modal";
import { AgvanceUtils } from "~/admin/setup/customer/agvance-utils";
import { LockIcon } from "~/core/icons";

export class FieldBoundaryPanel_ extends Component {
    static propTypes = {
        agvanceFieldList: PropTypes.array,
        agvanceOrgLevelList: PropTypes.array,
        canSubmit: PropTypes.bool.isRequired,
        closeImportWizard: PropTypes.func.isRequired,
        fetchAgvanceFieldList: PropTypes.func.isRequired,
        fetchAgvanceOrgLevelList: PropTypes.func,
        onSetFieldsToResurface: PropTypes.func,
        getImportFileInfo: PropTypes.func.isRequired,
        ignoreFarm: PropTypes.bool.isRequired,
        importFieldBoundaries: PropTypes.array,
        importFieldList: PropTypes.array,
        intl: intlShape.isRequired,
        isLockCustomer: PropTypes.bool,
        loadDetailPage: PropTypes.func,
        personalityId: PropTypes.number.isRequired,
        selectedImportFieldIndex: PropTypes.number.isRequired,
        setIgnoreFarm: PropTypes.func.isRequired,
        setImportFieldList: PropTypes.func.isRequired,
        setSelectedImportFieldIndex: PropTypes.func.isRequired,
        updateCanSubmit: PropTypes.func.isRequired,
        updateImportBoundary: PropTypes.func.isRequired,
        updateImportFileInfos: PropTypes.func.isRequired,
    };

    constructor(props) {
        super(props);
        this.state = {
            agvanceFieldList: [],
            customerGuid: null,
            editingRecord: -1,
            isAgvanceCustomer: AgvanceUtils.isAgvanceConnected(this.props.personalityId),
            fieldGuids: [],
            importFieldStrips: [],
        };
    }

    componentDidMount() {
        this._hackyRefresh();
    }

    componentDidUpdate(prevProps) {
        if (this.props.importFieldList.length === 0 && prevProps.importFieldList.length !== 0) {
            this.props.closeImportWizard();
        }

        if (!this.props.importFieldList || !prevProps.importFieldList) {
            this.props.setSelectedImportFieldIndex(-1);
        }

        const isValid = this.isValidImport(
            this.props.importFieldList,
            this.props.importFieldBoundaries
        );
        if (this.props.canSubmit !== isValid) {
            this.props.updateCanSubmit(isValid);
        }

        if (this.props.agvanceFieldList !== prevProps.agvanceFieldList) {
            this.setState({
                agvanceFieldList: this.props.agvanceFieldList,
            });
        }

        if (this.props.agvanceOrgLevelList !== prevProps.agvanceOrgLevelList) {
            this.setState({
                agvanceOrgLevelList: this.props.agvanceOrgLevelList,
            });
        }

        const boundariesChanged =
            this.props.importFieldBoundaries &&
            JSON.stringify(this.props.importFieldBoundaries) !==
                JSON.stringify(prevProps.importFieldBoundaries);
        const fieldsChanged =
            this.props.importFieldList &&
            JSON.stringify(this.props.importFieldList) !==
                JSON.stringify(prevProps.importFieldList);

        if (
            (this.props.importFieldBoundaries?.length > 0 &&
                this.props.importFieldList?.length > 0 &&
                this.state.importFieldStrips.length === 0) ||
            boundariesChanged ||
            fieldsChanged
        ) {
            this.setState({
                importFieldStrips: [],
            });
            this.getFieldInfoStrip(this.props.importFieldBoundaries, this.props.importFieldList);
        }
    }

    _hackyRefresh() {
        // 100% Hack to force the UI to recalculate the flex heights and respect them in IE/FF
        // NOTE: this.forceUpdate() does not do what we want
        const { selectedImportFieldIndex } = this.props;
        setTimeout(() => {
            this.props.setSelectedImportFieldIndex(-1);
            setTimeout(() => {
                this.props.setSelectedImportFieldIndex(selectedImportFieldIndex);
            });
        });
    }

    isRecordSelected(index) {
        return index === this.props.selectedImportFieldIndex;
    }

    isValidImport(importFieldList, importFieldBoundaries) {
        return (
            importFieldList &&
            importFieldBoundaries &&
            importFieldList.some((fb) => {
                const ifb = importFieldBoundaries.filter((boundary) => {
                    return (
                        fb.importFieldBoundaryGuidList.includes(boundary.importFieldBoundaryGuid) &&
                        ((!boundary.isSpatialConflict &&
                            !boundary.isNameConflict &&
                            !boundary.isUpdate) ||
                            (boundary.isUpdate &&
                                !boundary.isSpatialConflict &&
                                !boundary.isLockedBoundary &&
                                boundary.matchedFieldGuid !== null))
                    );
                });
                return (
                    ifb.length === fb.importFieldBoundaryGuidList.length &&
                    fb.customerGuid &&
                    fb.fieldName
                );
            })
        );
    }

    onFieldBoundaryMatched(fieldGuid) {
        this.setState(
            {
                fieldGuids: [...this.state.fieldGuids, fieldGuid],
            },
            () => {
                this.props.onSetFieldsToResurface(this.state.fieldGuids);
            }
        );
    }

    onCloseEditCustomerField() {
        this.setState({
            editingRecord: -1,
            customerGuid: "",
            customerName: "",
        });
    }

    onEditCustomerField(index) {
        this.setState({
            editingRecord: index,
        });
        if (this.props.selectedImportFieldIndex !== index) {
            this.props.setSelectedImportFieldIndex(index);
        }
    }

    onCustomerClose = () => {
        this.setState({
            editingRecord: this.state.prevEditingRecord,
            prevEditingRecord: -1,
        });
    };

    onCustomerSaveSuccess = (customerData) => {
        if (this.state.isAgvanceCustomer) {
            this.props.fetchAgvanceFieldList(customerData.customerGuid);
        }
        this.setState({
            customerGuid: customerData.customerGuid,
            customerName: customerData.name,
        });
    };

    onCustomerSaveFailed = (customerData) => {
        console.assert(Object.keys(customerData).length > 0);
        console.error("There was a problem while saving the customer", customerData);
    };

    onAddNewCustomer = (customerName) => {
        this.setState({
            editingRecord: -1,
            prevEditingRecord: this.state.editingRecord,
        });
        this.props.loadDetailPage("213", "101", {
            onCloseSlidingPanel: this.onCustomerClose,
            onSaveSuccess: this.onCustomerSaveSuccess,
            onSaveFailed: this.onCustomerSaveFailed,
            customerName,
        });
    };

    onRemoveField(index, fieldGuid) {
        let newImportFieldList = [
            ...this.props.importFieldList.slice(0, index),
            ...this.props.importFieldList.slice(index + 1),
        ];

        if (this.isRecordSelected(index)) {
            this.props.setSelectedImportFieldIndex(-1);
        }
        this.setState(
            { fieldGuids: this.state.fieldGuids.filter((guid) => guid !== fieldGuid) },
            () => {
                this.props.onSetFieldsToResurface(this.state.fieldGuids);
            }
        );

        this.props.setImportFieldList(newImportFieldList);
    }

    onSaveEditCustomerField(data, callback) {
        const { getImportFileInfo, ignoreFarm, importFieldList, updateImportFileInfos } =
            this.props;
        const { editingRecord, isAgvanceCustomer } = this.state;
        const {
            editCustomerGuid,
            editCustomerName,
            editFarmName,
            editFieldName,
            fieldEditGeometries,
            fieldEditsChanged,
            agvanceFieldGuid,
            agvanceFieldOrgLevelGuid,
            agvanceCustomerGuid,
        } = data;
        const originalRecord = importFieldList[editingRecord];
        const originalBoundaryCount = importFieldList.reduce(
            (c, fb) => c + fb.importFieldBoundaryGuidList.length,
            0
        );
        const isClearingFarmName = editFarmName == null || editFarmName === "";

        const agvanceCustomerInfo = {
            agvanceFieldOrgLevelGuid,
            agvanceCustomerGuid,
        };
        const agvanceData = isAgvanceCustomer
            ? agvanceFieldGuid != null
                ? { agvanceFieldGuid, ...agvanceCustomerInfo }
                : agvanceCustomerInfo
            : {};

        const request = {
            batchUpdate: {
                customerGuid: editCustomerGuid,
                where: {
                    importFileGuidList: Array.from(
                        new Set(importFieldList.map((f) => f.importFileGuid))
                    ),
                    oldCustomerName: originalRecord.customerName,
                },
            },
        };
        if (editFarmName !== originalRecord.farmName) {
            if (isClearingFarmName) {
                request.batchUpdate.importFileGuid = originalRecord.importFileGuid;
                request.batchUpdate.fieldName = originalRecord.fieldName;
            }
            request.batchUpdate.where.oldFarmName = originalRecord.farmName;
            request.batchUpdate.farmName = editFarmName || "";
        }
        if (isAgvanceCustomer) {
            request.batchUpdate.agvanceCustomerGuid = agvanceCustomerGuid;
            request.batchUpdate.agvanceOrgLevelGuid = agvanceFieldOrgLevelGuid;
        }
        let newArea = importFieldList[editingRecord]?.acres;
        if (
            editFieldName !== originalRecord.fieldName ||
            fieldEditsChanged ||
            (isAgvanceCustomer &&
                (agvanceCustomerGuid !== originalRecord.agvanceCustomerGuid ||
                    originalRecord.fieldName.length > 30))
        ) {
            request.fieldUpdate = {
                newFieldName:
                    isAgvanceCustomer && editFieldName.length > 30
                        ? editFieldName.substring(0, 30)
                        : editFieldName,
                where: {
                    importFileGuid: originalRecord.importFileGuid,
                    customerName: editCustomerName,
                    farmName: editFarmName,
                    fieldName: originalRecord.fieldName,
                },
            };
            if (isAgvanceCustomer && agvanceFieldGuid != null) {
                request.fieldUpdate.agvanceFieldGuid = agvanceFieldGuid;
            }
            if (fieldEditsChanged && fieldEditGeometries.length > 0) {
                request.fieldUpdate.polygons = fieldEditGeometries.map((g) => g.toJSON());
                newArea = fieldEditGeometries.reduce(
                    (accumulator, g) => accumulator + GeometryUtils.calculateArea(g),
                    0
                );
            }
        }

        request.ignoreFarm = ignoreFarm;

        this.props.updateImportBoundary(request, (importFieldBoundaryGuidList) => {
            const editRecord = {
                ...importFieldList[editingRecord],
                fieldName:
                    isAgvanceCustomer && editFieldName.length > 30
                        ? editFieldName.substring(0, 30)
                        : editFieldName,
                farmName: editFarmName,
                customerName: editCustomerName,
                customerGuid: editCustomerGuid,
                acres: newArea,
                ...agvanceData,
            };
            if (importFieldBoundaryGuidList) {
                editRecord.importFieldBoundaryGuidList = importFieldBoundaryGuidList;
            }
            // update customer and farm name locally without the need to fetch
            const updatedRecords = importFieldList.map((field) => ({
                ...field,
                customerName:
                    field.customerName === originalRecord.customerName
                        ? editCustomerName
                        : field.customerName,
                customerGuid:
                    field.customerName === originalRecord.customerName
                        ? editCustomerGuid
                        : field.customerGuid,
                farmName:
                    !isClearingFarmName &&
                    field.farmName === originalRecord.farmName &&
                    editFarmName !== originalRecord.farmName
                        ? editFarmName
                        : field.farmName,
                fieldName:
                    isAgvanceCustomer && editFieldName.length > 30
                        ? editFieldName.substring(0, 30)
                        : field.fieldName === originalRecord.fieldName
                        ? editFieldName
                        : field.fieldName,
            }));
            const newImportData = [
                ...updatedRecords.slice(0, editingRecord),
                editRecord,
                ...updatedRecords.slice(editingRecord + 1),
            ];

            const newBoundaryCount = newImportData.reduce(
                (c, fb) => c + fb.importFieldBoundaryGuidList.length,
                0
            );
            const delta = newBoundaryCount - originalBoundaryCount;
            if (delta !== 0) {
                const importFileInfo = getImportFileInfo(originalRecord.importFileGuid);
                updateImportFileInfos([
                    Object.assign(new ImportFileInfo(), {
                        ...importFileInfo,
                        totalPoints: importFileInfo.totalPoints + delta,
                    }),
                ]);
            }

            this.props.setImportFieldList(newImportData);
            this.setState({
                importFieldStrips: [],
            });
            if (callback) {
                callback(importFieldBoundaryGuidList);
            }
        });
    }

    toggleRecord(evt, index) {
        if (
            this.state.editingRecord === -1 &&
            !(
                evt.target.className.indexOf("context-menu") > -1 ||
                ["menu-dots", "menu-dot", "menu-item"].includes(evt.target.className) ||
                evt.target.parentNode.className === "menu-item"
            )
        ) {
            const newSelected = this.isRecordSelected(index) ? -1 : index;
            this.props.setSelectedImportFieldIndex(newSelected);
        }
    }

    getFieldInfoStrip(importFieldBoundaries, importFieldList) {
        const fieldGuids = [];

        if (importFieldBoundaries.length === 0 || importFieldList.length === 0) {
            return;
        }
        //TODO (post-remediation): consider refactoring this to use an accordion
        const importFieldStrips = importFieldList.map((fieldBoundary, index) => {
            const isUpdate = fieldBoundary.isUpdate;
            const isNoMatch = fieldBoundary.customerGuid === "";
            const isSpatialConflict = importFieldBoundaries.some((fb) => {
                return (
                    fieldBoundary.importFieldBoundaryGuidList.includes(
                        fb.importFieldBoundaryGuid
                    ) && fb.isSpatialConflict
                );
            });
            const matchedBoundary = importFieldBoundaries.find((fb) => {
                return fieldBoundary.importFieldBoundaryGuidList.includes(
                    fb.importFieldBoundaryGuid
                );
            });
            const isLockedBoundary =
                isUpdate && matchedBoundary ? matchedBoundary.isLockedBoundary : false;
            const noMatchForUpdate =
                (isUpdate && (!matchedBoundary || matchedBoundary?.matchedFieldGuid === null)) ||
                isNoMatch;

            const isValidUpdate =
                isUpdate && !isLockedBoundary && !noMatchForUpdate && !isSpatialConflict;
            const hasBoundaryMatched =
                matchedBoundary && matchedBoundary?.matchedFieldGuid !== null;

            if (isValidUpdate && hasBoundaryMatched) {
                fieldGuids.push(matchedBoundary?.matchedFieldGuid);
            }
            return {
                index,
                isUpdate,
                isNoMatch,
                isSpatialConflict,
                isLockedBoundary,
                noMatchForUpdate,
                isValidUpdate,
                hasBoundaryMatched,
                matchedBoundary,
                fieldBoundary,
            };
        });
        if (fieldGuids.length > 0) {
            this.props.onSetFieldsToResurface(fieldGuids);
        }
        if (importFieldStrips.length > 0) {
            this.setState({
                importFieldStrips,
            });
        }
    }

    renderFieldInfoStrips() {
        const { formatMessage, formatNumber } = this.props.intl;
        const listToRender =
            !this.props.importFieldList && this.props.importFieldBoundaries?.length
                ? null
                : this.state.importFieldStrips.map((fieldItem) => {
                      const {
                          hasBoundaryMatched,
                          index,
                          isNoMatch,
                          isLockedBoundary,
                          isSpatialConflict,
                          isUpdate,
                          isValidUpdate,
                          fieldBoundary,
                          matchedBoundary,
                          noMatchForUpdate,
                      } = fieldItem;
                      const isEditing = this.state.editingRecord === index;
                      let importFieldList = this.props.importFieldList[this.state.editingRecord];

                      if (this.state.customerGuid && this.state.customerName) {
                          importFieldList = {
                              ...importFieldList,
                              customerGuid: this.state.customerGuid,
                              customerName: this.state.customerName,
                          };
                      }
                      if (isValidUpdate && hasBoundaryMatched) {
                          importFieldList = {
                              ...importFieldList,
                              fieldGuid: matchedBoundary?.matchedFieldGuid,
                          };
                      }
                      const contextMenuItems = [
                          {
                              key: 0,
                              label: formatMessage(messages.editCustomerFieldText),
                              action: () => this.onEditCustomerField(index),
                              disabled: isUpdate && matchedBoundary?.isLockedBoundary,
                          },
                          {
                              key: 1,
                              label: formatMessage(messages.removeFieldText),
                              action: () => this.onRemoveField(index),
                          },
                      ];
                      return (
                          <div
                              key={index}
                              className={classNames("ifb-row", {
                                  selected: this.isRecordSelected(index),
                              })}
                          >
                              <div
                                  className={classNames("ifb-strip", {
                                      editing: isEditing,
                                      "no-match": isNoMatch || noMatchForUpdate || isLockedBoundary,
                                      "spatial-conflict": isSpatialConflict,
                                      selected: this.isRecordSelected(index),
                                      "update-match": isValidUpdate,
                                  })}
                                  onClick={(evt) => this.toggleRecord(evt, index)}
                              >
                                  <div className="space field-selected-column">
                                      <div className="field-selected-div">
                                          <div className="field-selected-div"></div>
                                      </div>
                                  </div>
                                  <div
                                      className={classNames({
                                          column1: !isUpdate,
                                          "update-column1": isUpdate,
                                          "wide-column": this.props.ignoreFarm && !isUpdate,
                                          "update-wide-column": this.props.ignoreFarm & isUpdate,
                                      })}
                                      title={fieldBoundary.customerName}
                                  >
                                      {fieldBoundary.customerName}
                                  </div>
                                  {!this.props.ignoreFarm ? (
                                      <div
                                          className={classNames({
                                              column: !isUpdate,
                                              "update-column": isUpdate,
                                          })}
                                          title={fieldBoundary.farmName}
                                      >
                                          {fieldBoundary.farmName}
                                      </div>
                                  ) : null}
                                  <div
                                      className={classNames({
                                          column: !isUpdate,
                                          "update-column": isUpdate,
                                          "wide-column": this.props.ignoreFarm && !isUpdate,
                                          "update-wide-column": this.props.ignoreFarm & isUpdate,
                                      })}
                                      title={fieldBoundary.fieldName}
                                  >
                                      {isLockedBoundary ? <LockIcon className="lock-icon" /> : null}
                                      {fieldBoundary.fieldName}
                                  </div>
                                  <div
                                      className={classNames("acres-column", {
                                          "update-acres": isUpdate,
                                      })}
                                      title={fieldBoundary.acres}
                                  >
                                      {isUpdate ? (
                                          <React.Fragment>
                                              {hasBoundaryMatched ? (
                                                  <div
                                                      className="acres-old"
                                                      title={matchedBoundary.originalArea}
                                                  >
                                                      <div className="label">Old:</div>
                                                      <div className="value">
                                                          {formatNumber(
                                                              matchedBoundary.originalArea,
                                                              intlConfig.numberFormatOptions
                                                          )}
                                                      </div>
                                                  </div>
                                              ) : null}
                                              <div
                                                  className="acres-new"
                                                  title={fieldBoundary.acres}
                                              >
                                                  <div className="label">New: </div>
                                                  <div className="value">
                                                      {formatNumber(
                                                          fieldBoundary.acres,
                                                          intlConfig.numberFormatOptions
                                                      )}
                                                  </div>
                                              </div>
                                          </React.Fragment>
                                      ) : (
                                          formatNumber(
                                              fieldBoundary.acres,
                                              intlConfig.numberFormatOptions
                                          )
                                      )}
                                  </div>
                                  <div className="space">
                                      <Menu
                                          className={classNames("field-boundary-context-menu")}
                                          isDotMenu={true}
                                          menuItems={contextMenuItems}
                                      />
                                  </div>
                              </div>
                              {!this.isRecordSelected(index) ? null : (
                                  <div className="ifb-file" title={fieldBoundary.importFileName}>
                                      <div className="ifb-file-name">
                                          {fieldBoundary.importFileName}
                                      </div>
                                  </div>
                              )}
                              {index !== this.state.editingRecord ? null : (
                                  <AddEditFieldModal
                                      record={importFieldList}
                                      onSave={(data, callback) =>
                                          this.onSaveEditCustomerField(data, () => {
                                              this._hackyRefresh();
                                              callback();
                                          })
                                      }
                                      onClose={() => this.onCloseEditCustomerField()}
                                      isUpdateField={isUpdate}
                                      toolsetPayload={{
                                          fieldGuid: hasBoundaryMatched
                                              ? matchedBoundary?.matchedFieldGuid
                                              : null,
                                          customerGuid: this.props.importFieldList[
                                              this.state.editingRecord
                                          ]
                                              ? this.props.importFieldList[this.state.editingRecord]
                                                    .customerGuid
                                              : null,
                                          importFieldBoundaryGuidList: this.props.importFieldList[
                                              this.state.editingRecord
                                          ]
                                              ? this.props.importFieldList[this.state.editingRecord]
                                                    .importFieldBoundaryGuidList
                                              : [],
                                      }}
                                      onAddNewCustomer={this.onAddNewCustomer}
                                      fetchAgvanceFieldList={this.props.fetchAgvanceFieldList}
                                      isAgvanceCustomer={this.state.isAgvanceCustomer}
                                      agvanceFieldList={this.state.agvanceFieldList}
                                      fetchAgvanceOrgLevelList={this.props.fetchAgvanceOrgLevelList}
                                      agvanceOrgLevelList={this.state.agvanceOrgLevelList}
                                      removeImportFieldBoundary={() =>
                                          this.onRemoveField(this.state.editingRecord)
                                      }
                                      isLockCustomer={false}
                                  />
                              )}
                          </div>
                      );
                  });
        return listToRender;
    }

    render() {
        const { formatMessage } = this.props.intl;
        const { importFieldList } = this.props;

        const importFileListGuids = importFieldList.map((field) => field.importFileGuid);
        const isUpdate = importFieldList.some((field) => field.isUpdate);
        return (
            <div className="field-boundary-panel">
                <div className="ignore-farm-checkbox">
                    <Checkbox
                        value={this.props.ignoreFarm}
                        label={formatMessage(messages.ignoreFarm)}
                        onChange={(evt, val) => this.props.setIgnoreFarm(importFileListGuids, val)}
                    />
                </div>
                <Bucket isExpanded={true} isCollapsible={false} showSymbol={false}>
                    <BucketHeader className={"import-field-boundary-header"}>
                        <div className="space"></div>
                        <div
                            className={classNames({
                                column1: !isUpdate,
                                "update-column1": isUpdate,
                                "wide-column": this.props.ignoreFarm && !isUpdate,
                                "update-wide-column": this.props.ignoreFarm & isUpdate,
                            })}
                        >
                            Customer
                        </div>
                        {this.props.ignoreFarm ? null : (
                            <div
                                className={classNames({
                                    column: !isUpdate,
                                    "update-column": isUpdate,
                                })}
                            >
                                Farm
                            </div>
                        )}
                        <div
                            className={classNames({
                                column: !isUpdate,
                                "update-column": isUpdate,
                                "wide-column": this.props.ignoreFarm && !isUpdate,
                                "update-wide-column": this.props.ignoreFarm & isUpdate,
                            })}
                        >
                            Field
                        </div>
                        <div className="acres-column">Acres</div>
                        <div className="space"></div>
                    </BucketHeader>
                    {this.renderFieldInfoStrips()}
                </Bucket>
            </div>
        );
    }
}
